import { useState } from "react";
import Portrait from "@/components/Portrait";
import {
  SKIN_TONES, HAIR_COLORS, EYE_COLORS, CLOAK_COLORS, HAIR_STYLES, MARKINGS,
} from "@/components/Portrait";
import { GENDERS } from "@/lib/classChibi";
import { ArrowLeft, ArrowRight, Shuffle } from "lucide-react";

/**
 * Orna-inspired minimalist character creator. Six categorical sliders let
 * the player commit to a face. The `Portrait` component re-renders live from
 * the current appearance object.
 *
 * Props:
 *   name    : chosen username (for the live preview label)
 *   value   : current appearance {skin, hair, hair_color, eyes, cloak, marking}
 *   onChange: setter for the appearance object
 *   onBack  : go back to the name step
 *   onNext  : proceed to class step
 */
const FIELDS = [
  { key: "gender",     label: "Sex",        values: GENDERS,                   swatch: null },
  { key: "skin",       label: "Skin",       values: Object.keys(SKIN_TONES),   swatch: SKIN_TONES },
  { key: "hair",       label: "Hair",       values: HAIR_STYLES,               swatch: null },
  { key: "hair_color", label: "Hair Color", values: Object.keys(HAIR_COLORS),  swatch: HAIR_COLORS },
  { key: "eyes",       label: "Eyes",       values: Object.keys(EYE_COLORS),   swatch: EYE_COLORS },
  { key: "cloak",      label: "Cloak",      values: Object.keys(CLOAK_COLORS), swatch: CLOAK_COLORS },
  { key: "marking",    label: "Marking",    values: MARKINGS,                  swatch: null },
];

function rand(a) { return a[Math.floor(Math.random() * a.length)]; }

export function defaultAppearance() {
  return {
    gender: "male",
    skin: "olive",
    hair: "short",
    hair_color: "raven",
    eyes: "ember",
    cloak: "blood",
    marking: "none",
    // Heterochromia (Session 9, spec 35). Empty string = mirror `eyes`.
    eye_left: "",
    eye_right: "",
  };
}

export default function CharacterCreator({ name, value, onChange, onBack, onNext }) {
  const v = value || defaultAppearance();

  const setField = (key, newVal) => {
    onChange({ ...v, [key]: newVal });
  };

  const randomize = () => {
    const next = {};
    for (const f of FIELDS) next[f.key] = rand(f.values);
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="onboarding-appearance">
      <div className="panel p-4 flex items-center gap-4">
        <Portrait name={name || "you"} appearance={v} size={96} />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c]">
            Preview
          </div>
          <div className="font-heading text-lg text-[#E2DFD8] truncate">{name || "Wanderer"}</div>
          <div className="font-mono text-[10px] text-[#666] uppercase tracking-wider mt-1">
            {v.gender === "female" ? "female" : "male"} · {v.hair === "hood" ? "hooded" : v.hair} · {v.hair_color} hair · {v.eyes} eyes
          </div>
          <button
            data-testid="onboarding-appearance-randomize"
            onClick={randomize}
            className="mt-2 px-2 py-1 border border-[#B89C3A] text-[#E5C34A] font-heading text-[9px] tracking-widest uppercase flex items-center gap-1 hover:bg-[#B89C3A]/10"
          >
            <Shuffle className="w-3 h-3" strokeWidth={1.5} /> Randomize
          </button>
        </div>
      </div>

      <div className="panel divide-y divide-[#1a1a1a]">
        {FIELDS.map(f => (
          <div key={f.key} className="p-3" data-testid={`appearance-row-${f.key}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c]">
                {f.label}
              </span>
              <span className="font-mono text-[10px] text-[#B0ADA5] tracking-widest uppercase">
                {v[f.key]}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {f.values.map(val => {
                const active = v[f.key] === val;
                if (f.swatch) {
                  return (
                    <button
                      key={val}
                      data-testid={`swatch-${f.key}-${val}`}
                      onClick={() => setField(f.key, val)}
                      aria-label={`${f.label} ${val}`}
                      className={`w-7 h-7 border transition-all ${active ? "border-2" : "border"}`}
                      style={{
                        background: f.swatch[val],
                        borderColor: active ? "#E55A00" : "#333",
                        boxShadow: active ? "0 0 6px rgba(229,90,0,0.6)" : "none",
                      }}
                    />
                  );
                }
                return (
                  <button
                    key={val}
                    data-testid={`opt-${f.key}-${val}`}
                    onClick={() => setField(f.key, val)}
                    className={`px-2 py-1 border font-heading text-[9px] tracking-widest uppercase transition-colors ${
                      active
                        ? "border-[#E55A00] text-[#E55A00]"
                        : "border-[#262626] text-[#8c8c8c] hover:border-[#B89C3A] hover:text-[#E5C34A]"
                    }`}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Heterochromia (Session 9, spec 35). Only shown if the user opts in. */}
      <HeterochromiaSection value={v} setField={setField} />

      {/* Visual FX preferences — accessible from the very first screen so
          players can dial down effects before ever entering combat (spec 37) */}
      <VisualPrefsSection />

      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid="onboarding-appearance-back"
          onClick={onBack}
          className="py-3 border border-[#262626] text-[#8c8c8c] font-heading text-xs tracking-widest uppercase hover:text-[#E2DFD8] flex items-center justify-center gap-2"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back
        </button>
        <button
          data-testid="onboarding-appearance-next"
          onClick={onNext}
          className="py-3 border border-[#E55A00] text-[#E55A00] font-heading text-xs tracking-widest uppercase hover:bg-[#E55A00]/10 flex items-center justify-center gap-2"
        >
          Continue <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}


// ── Heterochromia (spec 35) ──────────────────────────────────────

function HeterochromiaSection({ value, setField }) {
  const [enabled, setEnabled] = useState(
    Boolean(value.eye_left || value.eye_right)
  );
  const toggle = () => {
    if (enabled) {
      setField("eye_left", "");
      setField("eye_right", "");
    } else {
      setField("eye_left", value.eyes);
      setField("eye_right", value.eyes);
    }
    setEnabled(!enabled);
  };

  return (
    <div className="panel p-3 space-y-2" data-testid="heterochromia-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c]">Heterochromia</div>
          <div className="italic text-[10px] text-[#666] mt-0.5">Different colours for each eye</div>
        </div>
        <button
          data-testid="heterochromia-toggle"
          onClick={toggle}
          className={`px-3 py-1 border font-heading text-[10px] tracking-widest uppercase transition-colors
            ${enabled ? "border-[#B89C3A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-[#262626] text-[#8c8c8c]"}`}>
          {enabled ? "On" : "Off"}
        </button>
      </div>

      {enabled && (
        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-[#161616]">
          {[
            { key: "eye_left",  label: "Left" },
            { key: "eye_right", label: "Right" },
          ].map(f => (
            <div key={f.key}>
              <div className="font-mono text-[9px] tracking-widest uppercase text-[#666] mb-1">{f.label} Eye</div>
              <div className="flex flex-wrap gap-1">
                {Object.keys(EYE_COLORS).map(col => (
                  <button
                    key={col}
                    data-testid={`hetero-${f.key}-${col}`}
                    onClick={() => setField(f.key, col)}
                    className={`w-6 h-6 border transition-colors
                      ${value[f.key] === col ? "border-[#E5C34A]" : "border-[#262626] hover:border-[#B89C3A]"}`}
                    style={{ backgroundColor: EYE_COLORS[col] }}
                    title={col}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Visual Effects preferences (spec 36-37) ──────────────────────

const VISUAL_FX_KEYS = [
  { key: "screen_shake",     label: "Screen Shake",     desc: "Camera kick on crits and boss hits" },
  { key: "flash_effects",    label: "Flash Effects",    desc: "Bright screen flashes" },
  { key: "damage_numbers",   label: "Damage Numbers",   desc: "Floating combat text" },
  { key: "combat_particles", label: "Combat Particles", desc: "Sparks and dust" },
  { key: "reduced_motion",   label: "Reduced Motion",   desc: "Simpler transitions" },
];

function VisualPrefsSection() {
  // Client-only until the character exists; persisted on first save via
  // /api/character/preferences later in the onboarding flow.
  const [prefs, setPrefs] = useState({
    screen_shake: true, flash_effects: true, damage_numbers: true,
    combat_particles: true, reduced_motion: false,
  });
  // Expose for the parent Onboarding to persist after character creation.
  if (typeof window !== "undefined") {
    window.__gw_visual_prefs = prefs;
  }
  return (
    <div className="panel p-3 space-y-2" data-testid="visual-prefs-section">
      <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c]">Visual Preferences</div>
      <div className="italic text-[10px] text-[#666]">
        Fine-tune combat effects. You can change these later in Settings.
      </div>
      <div className="space-y-1 pt-1">
        {VISUAL_FX_KEYS.map(f => {
          const on = prefs[f.key];
          return (
            <button
              key={f.key}
              data-testid={`vfx-${f.key}`}
              onClick={() => setPrefs(p => ({ ...p, [f.key]: !p[f.key] }))}
              className="w-full flex items-center justify-between panel-inner px-3 py-1.5 text-left hover:border-[#B89C3A]">
              <div>
                <div className="font-heading text-xs text-[#E2DFD8]">{f.label}</div>
                <div className="font-mono text-[9px] text-[#666]">{f.desc}</div>
              </div>
              <span className={`font-mono text-[10px] uppercase tracking-widest ${on ? "text-[#6E8B3D]" : "text-[#666]"}`}>
                {on ? "On" : "Off"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
